import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Download } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { OmCard, OmHead, OmTable, Row, Td, Pill, statusTone, GOLD, MUTED, NAVY, FIELD } from "@/components/om/OmBits";

const today = () => new Date().toISOString().slice(0, 10);

export default function OmDailyLog() {
  const [rows, setRows] = useState([]);
  const [assets, setAssets] = useState([]);
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ asset_id: "", date: today(), shift: "Morning", site_location: "", observations: "", readings: [] });

  const load = () => api.get("/om/daily-logs").then(({ data }) => setRows(data));
  useEffect(() => {
    load();
    api.get("/om/assets").then(({ data }) => setAssets(data));
    api.get("/om/meta").then(({ data }) => setMeta(data));
  }, []);

  const pickAsset = (id) => {
    const a = assets.find((x) => x.id === id);
    const params = meta?.parameters?.[a?.category] || meta?.parameters?.General || [];
    setForm({
      ...form, asset_id: id, site_location: a?.site || "",
      readings: params.map(([parameter, unit, lo, hi]) => ({ parameter, unit, expected_min: lo, expected_max: hi, actual: "" })),
    });
  };

  const save = async () => {
    try {
      await api.post("/om/daily-logs", {
        ...form,
        readings: form.readings.map((r) => ({ ...r, actual: Number(r.actual || 0) })),
      });
      toast.success("Shift log recorded — out-of-range readings alert the O&M Manager");
      setOpen(false);
      setForm({ asset_id: "", date: today(), shift: "Morning", site_location: "", observations: "", readings: [] });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const exportCsv = () => {
    const lines = ["Date,Shift,Asset,Severity,Logged by,Parameter,Unit,Expected,Actual,Status"];
    rows.forEach((r) => r.readings.forEach((d) => lines.push(
      [r.date, r.shift, `"${r.asset_name}"`, r.severity, r.logged_by, `"${d.parameter}"`, d.unit,
        `${d.expected_min}-${d.expected_max}`, d.actual, d.status].join(","))));
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
    a.download = "om_daily_monitoring_log.csv";
    a.click();
  };

  const filtered = rows.filter((r) => !q || `${r.date} ${r.asset_name} ${r.shift} ${r.logged_by}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="om-daily-log-page">
      <OmHead title="Daily Monitoring Log" subtitle="Shift readings per asset, graded against the expected operating range.">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search date, asset, shift, user" className="w-[230px]" style={FIELD} data-testid="om-log-search" />
        <Button variant="outline" onClick={exportCsv} style={{ borderColor: `${GOLD}66`, color: GOLD, background: "transparent" }} data-testid="om-log-export-btn">
          <Download className="w-4 h-4 mr-1.5" /> Export
        </Button>
        <Button onClick={() => setOpen(true)} className="font-semibold" style={{ background: GOLD, color: NAVY, borderRadius: 8 }} data-testid="om-log-add-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Log readings
        </Button>
      </OmHead>

      <OmCard>
        <OmTable head={["Date", "Shift", "Asset", "Readings", "Out of range", "Severity", "Logged by"]} testid="om-log-table">
          {filtered.map((r, i) => (
            <Row key={r.id} i={i} testid={`om-log-row-${r.id}`}>
              <Td className="tabular-nums">{r.date}</Td>
              <Td>{r.shift}</Td>
              <Td><span style={{ color: MUTED }}>{r.asset_code}</span> {r.asset_name}</Td>
              <Td className="tabular-nums">{r.readings.length}</Td>
              <Td className="max-w-[320px]">
                {r.readings.filter((d) => d.status !== "Normal").map((d) => `${d.parameter} ${d.actual}${d.unit}`).join(", ") || "—"}
              </Td>
              <Td><Pill tone={statusTone(r.severity)}>{r.severity}</Pill></Td>
              <Td style={{ color: MUTED }}>{r.logged_by}</Td>
            </Row>
          ))}
          {filtered.length === 0 ? <Row i={0}><Td className="text-[12.5px]">No shift logs yet.</Td></Row> : null}
        </OmTable>
      </OmCard>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" style={{ background: "#112240", borderColor: `${GOLD}33` }}>
          <DialogHeader><DialogTitle className="text-white">Shift monitoring entry</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="text-[11px] uppercase" style={{ color: MUTED }}>Asset</Label>
              <select value={form.asset_id} onChange={(e) => pickAsset(e.target.value)} className="w-full h-9 px-2 text-[13px] border" style={{ ...FIELD, borderRadius: 6 }} data-testid="om-log-asset-select">
                <option value="">Select asset</option>
                {assets.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name} ({a.category})</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase" style={{ color: MUTED }}>Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} style={FIELD} data-testid="om-log-date-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase" style={{ color: MUTED }}>Shift</Label>
              <select value={form.shift} onChange={(e) => setForm({ ...form, shift: e.target.value })} className="w-full h-9 px-2 text-[13px] border" style={{ ...FIELD, borderRadius: 6 }} data-testid="om-log-shift-select">
                {["Morning", "Afternoon", "Night"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="text-[11px] uppercase" style={{ color: MUTED }}>Site location</Label>
              <Input value={form.site_location} onChange={(e) => setForm({ ...form, site_location: e.target.value })} style={FIELD} data-testid="om-log-location-input" />
            </div>
          </div>

          {form.readings.length ? (
            <div className="mt-2 border" style={{ borderColor: `${GOLD}22`, borderRadius: 8 }}>
              <div className="px-3 py-2 text-[10.5px] uppercase tracking-[0.12em]" style={{ color: GOLD, borderBottom: `1px solid ${GOLD}22` }}>
                Sector parameters — readings outside the expected range raise an alert
              </div>
              {form.readings.map((r, i) => (
                <div key={r.parameter} className="flex items-center gap-3 px-3 py-2">
                  <span className="text-white text-[12.5px] flex-1">{r.parameter} <span style={{ color: MUTED }}>({r.unit})</span></span>
                  <span className="text-[11px] tabular-nums" style={{ color: MUTED }}>{r.expected_min}–{r.expected_max}</span>
                  <Input
                    value={r.actual}
                    onChange={(e) => setForm({ ...form, readings: form.readings.map((x, j) => j === i ? { ...x, actual: e.target.value } : x) })}
                    className="w-[100px] h-8" style={FIELD} placeholder="actual"
                    data-testid={`om-log-reading-${i}`}
                  />
                </div>
              ))}
            </div>
          ) : null}

          <div className="space-y-1.5 mt-2">
            <Label className="text-[11px] uppercase" style={{ color: MUTED }}>Observations</Label>
            <Textarea rows={2} value={form.observations} onChange={(e) => setForm({ ...form, observations: e.target.value })} style={FIELD} data-testid="om-log-observations-input" />
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={!form.asset_id} className="font-semibold" style={{ background: GOLD, color: NAVY }} data-testid="om-log-save-btn">Submit shift log</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
